import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import { getEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import md5 from 'crypto-js/md5';
import { useCallback, useEffect, useRef } from 'react';

const PAGE_NAME = '202605太阳镜专题活动页';
const MARKET_ACTIVITY_NAME = '202605太阳镜专题活动页';

export function useSunglassesTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  const enterTimer = useRef<number>(Date.now());
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });
  // 缓存路由信息，避免 getFromRouterInfo 引用变化导致 trackPageView 重建
  const routerInfoRef = useRef(getFromRouterInfo());

  const marketActivityId = md5('SunglassesSale202605').toString();

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_View
   * @EventDesc 太阳镜专题活动页浏览
   */
  const trackPageView = useCallback(() => {
    const { refer_page_name, refer_url_path } = routerInfoRef.current;
    enterTimer.current = Date.now();

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_View',
          properties: {
            page_name: PAGE_NAME,
            refer_page_name,
            refer_url_path,
            entrance_page_scene: getEntrancePageScene({
              referPageName: refer_page_name,
              referUrlPath: refer_url_path,
            }),
            market_activity_id: marketActivityId,
            market_activity_name: MARKET_ACTIVITY_NAME,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Sunglasses page view tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_Leave
   * @EventDesc 太阳镜专题活动页离开
   */
  const trackPageLeave = useCallback(() => {
    const { viewAdvance, viewArea } = lastScrollState.current;

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_Leave',
          properties: {
            page_name: PAGE_NAME,
            view_advance: viewAdvance,
            view_duration: Math.ceil((Date.now() - enterTimer.current) / 1000),
            view_area: viewArea,
            market_activity_id: marketActivityId,
            market_activity_name: MARKET_ACTIVITY_NAME,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Sunglasses page leave tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageNavTab_Click
   * @EventDesc 太阳镜专题活动页tab导航点击（仅二级导航选中）
   */
  const trackNavTabClick = useCallback(
    (tabName: string, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageNavTab_Click',
            properties: {
              page_name: PAGE_NAME,
              module_name: '导航位',
              pits_index: index,
              tab_name: tabName,
              market_activity_id: marketActivityId,
              market_activity_name: MARKET_ACTIVITY_NAME,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Sunglasses nav tab click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Click
   * @EventDesc 太阳镜专题活动页商品点击
   */
  const trackProductClick = useCallback(
    (item: any, index: number, moduleName: string, tabName?: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        const properties: Record<string, unknown> = {
          page_name: PAGE_NAME,
          module_name: moduleName,
          pits_index: index,
          product_sku: item.sku,
          business_sku: item.business_sku,
          shop_sku: item.shop_sku,
          product_id: `${item.id}`,
          product_name: item.short_name || item.name,
          product_price: parseFloat(nowPriceInfo.price || '0'),
          current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
          base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
          currency: currency?.name || '',
          market_activity_id: marketActivityId,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        if (tabName) {
          properties.tab_name = tabName;
        }

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Sunglasses product click tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Exp
   * @EventDesc 太阳镜专题活动页商品曝光
   */
  const trackProductExp = useCallback(
    (item: any, index: number, moduleName: string, tabName?: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        const properties: Record<string, unknown> = {
          page_name: PAGE_NAME,
          module_name: moduleName,
          pits_index: index,
          product_sku: item.sku,
          business_sku: item.business_sku,
          shop_sku: item.shop_sku,
          product_id: `${item.id}`,
          product_name: item.short_name || item.name,
          product_price: parseFloat(nowPriceInfo.price || '0'),
          current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
          base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
          currency: currency?.name || '',
          market_activity_id: marketActivityId,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        if (tabName) {
          properties.tab_name = tabName;
        }

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Sunglasses product exp tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  // 监听滚动更新状态
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        const viewArea = getViewArea();
        lastScrollState.current = { viewAdvance, viewArea };
      } catch (error) {
        logger.error('Error updating scroll state:', error);
      }
    };

    updateScrollState();

    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackProductClick,
    trackProductExp,
  };
}
